// First-touch attribution capture for the marketing site (`eden.so`).
//
// Mirrors `apps/ui/website/src/attribution.ts` (the in-app twin) so a
// visitor who lands on `eden.so` first gets their UTMs captured here
// and the cookie crosses to `app.eden.so` automatically because we
// scope it to `.eden.so`. Logic and cookie format are identical to
// the in-app version — see that file for the full rationale on
// first-touch semantics, the FirstPromoter `via` synthesis, and the
// shape of the stored record.
//
// Convention reference: `marketing/utm-conventions.md`.

const COOKIE_NAME = "eden_attribution";
const COOKIE_DAYS = 30;

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"] as const;

type AttributionRecord = {
  source: string | null;
  medium: string | null;
  campaign: string | null;
  content: string | null;
  term: string | null;
  capturedAt: string;
  landingPath: string | null;
  referrer: string | null;
};

function readParamsFromUrl(): Partial<Record<(typeof UTM_KEYS)[number], string>> & {
  via?: string;
} {
  if (typeof window === "undefined") return {};
  const params = new URLSearchParams(window.location.search);
  const out: Partial<Record<(typeof UTM_KEYS)[number], string>> & { via?: string } = {};
  for (const key of UTM_KEYS) {
    const value = params.get(key)?.trim();
    if (value) out[key] = value;
  }
  const via = params.get("via")?.trim();
  if (via) out.via = via;
  return out;
}

function readCookie(): AttributionRecord | null {
  if (typeof document === "undefined") return null;
  for (const part of document.cookie.split(";")) {
    const [rawKey, ...rest] = part.split("=");
    if (rawKey?.trim() !== COOKIE_NAME) continue;
    try {
      const decoded = decodeURIComponent(rest.join("=").trim());
      if (!decoded) return null;
      const parsed = JSON.parse(decoded) as Partial<AttributionRecord>;
      return {
        source: parsed.source ?? null,
        medium: parsed.medium ?? null,
        campaign: parsed.campaign ?? null,
        content: parsed.content ?? null,
        term: parsed.term ?? null,
        capturedAt: parsed.capturedAt ?? new Date().toISOString(),
        landingPath: parsed.landingPath ?? null,
        referrer: parsed.referrer ?? null,
      };
    } catch {
      return null;
    }
  }
  return null;
}

function writeCookie(record: AttributionRecord): void {
  if (typeof document === "undefined") return;
  const host = window.location.hostname;
  const isEdenProd = host === "eden.so" || host.endsWith(".eden.so");
  const domain = isEdenProd ? "; Domain=.eden.so" : "";
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  const maxAge = COOKIE_DAYS * 24 * 60 * 60;
  const value = encodeURIComponent(JSON.stringify(record));
  document.cookie = `${COOKIE_NAME}=${value}; Max-Age=${maxAge}; Path=/${domain}; SameSite=Lax${secure}`;
}

/**
 * Capture first-touch attribution from the current URL. No-op when
 * the cookie is already present (first-touch wins) or when nothing
 * attribution-relevant is on the URL. Safe to call from
 * `gatsby-browser`'s `onClientEntry` — guards against Gatsby's
 * SSR build pass.
 */
export function captureLandingAttribution(): void {
  if (typeof window === "undefined") return;
  // The referral cookie has its own first-touch rule and lifetime.
  captureReferralCode();
  const params = readParamsFromUrl();
  // Last touch is written before the first-touch check: it moves on every tagged link.
  captureLastTouch(params);
  if (readCookie()) return;

  const hasAnyUtm = UTM_KEYS.some((key) => params[key]);
  const hasVia = Boolean(params.via);
  if (!hasAnyUtm && !hasVia) return;

  const source = params.utm_source ?? params.via ?? null;
  const medium = params.utm_medium ?? (hasVia && !hasAnyUtm ? "affiliate" : null);
  const campaign = params.utm_campaign ?? (hasVia && !hasAnyUtm ? "affiliate_evergreen" : null);

  writeCookie({
    source,
    medium,
    campaign,
    content: params.utm_content ?? null,
    term: params.utm_term ?? null,
    capturedAt: new Date().toISOString(),
    landingPath: window.location.pathname + window.location.search,
    referrer: document.referrer || null,
  });
}

// ── Last touch (the $5 pay-first checkout only) ─────────────────────────
//
// First touch wins in `eden_attribution`, so someone who first came from Dan's
// site and later buys from a new Meta ad would be credited to Dan's site.
// `eden_last_touch` keeps the LATEST tagged link for 7 days (Meta's click
// window) and rides into the pay-first Checkout as `attribution_last_*`
// (billing `revenueSaleSource.ts` names that ad on #revenue). Landing only:
// the app's checkouts keep first touch.
const LAST_TOUCH_COOKIE_NAME = "eden_last_touch";
const LAST_TOUCH_COOKIE_DAYS = 7;

type LastTouchRecord = Pick<
  AttributionRecord,
  "source" | "medium" | "campaign" | "content" | "term" | "capturedAt"
>;

function captureLastTouch(params: Partial<Record<(typeof UTM_KEYS)[number], string>>): void {
  if (!UTM_KEYS.some((key) => params[key])) return;
  const record: LastTouchRecord = {
    source: params.utm_source ?? null,
    medium: params.utm_medium ?? null,
    campaign: params.utm_campaign ?? null,
    content: params.utm_content ?? null,
    term: params.utm_term ?? null,
    capturedAt: new Date().toISOString(),
  };
  const host = window.location.hostname;
  const isEdenProd = host === "eden.so" || host.endsWith(".eden.so");
  const domain = isEdenProd ? "; Domain=.eden.so" : "";
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  const maxAge = LAST_TOUCH_COOKIE_DAYS * 24 * 60 * 60;
  const value = encodeURIComponent(JSON.stringify(record));
  document.cookie = `${LAST_TOUCH_COOKIE_NAME}=${value}; Max-Age=${maxAge}; Path=/${domain}; SameSite=Lax${secure}`;
}

function readLastTouch(): LastTouchRecord | null {
  const raw = readNamedCookie(LAST_TOUCH_COOKIE_NAME);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<LastTouchRecord>;
    return {
      source: parsed.source ?? null,
      medium: parsed.medium ?? null,
      campaign: parsed.campaign ?? null,
      content: parsed.content ?? null,
      term: parsed.term ?? null,
      capturedAt: parsed.capturedAt ?? "",
    };
  } catch {
    return null;
  }
}

// ── Self-serve referral link (`?via=eden-…`) ────────────────────────────
//
// Separate from `eden_attribution` on purpose: that cookie is a 30-day
// first-touch marketing record shared with UTMs, while a referral is a
// 60-day promise to the person who shared the link. First touch wins here
// too (a later `?via=` never replaces the first referrer), and the value is
// read by the app's bootstrap (apps/ui/website/src/attribution.ts twin), which hands it to the
// server for validation on a brand-new account's first bootstrap.
const REFERRAL_COOKIE_NAME = "eden_ref";
const REFERRAL_COOKIE_DAYS = 60;
const REFERRAL_CODE_RE = /^[a-z0-9][a-z0-9_.-]{1,63}$/i;

function readReferralCookie(): string | null {
  if (typeof document === "undefined") return null;
  for (const part of document.cookie.split(";")) {
    const [rawKey, ...rest] = part.split("=");
    if (rawKey?.trim() !== REFERRAL_COOKIE_NAME) continue;
    try {
      const value = decodeURIComponent(rest.join("=").trim());
      return REFERRAL_CODE_RE.test(value) ? value : null;
    } catch {
      return null;
    }
  }
  return null;
}

function writeReferralCookie(code: string): void {
  if (typeof document === "undefined") return;
  const host = window.location.hostname;
  const isEdenProd = host === "eden.so" || host.endsWith(".eden.so");
  const domain = isEdenProd ? "; Domain=.eden.so" : "";
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  const maxAge = REFERRAL_COOKIE_DAYS * 24 * 60 * 60;
  document.cookie = `${REFERRAL_COOKIE_NAME}=${encodeURIComponent(code)}; Max-Age=${maxAge}; Path=/${domain}; SameSite=Lax${secure}`;
}

// The link tag (`?tag=`, or FirstPromoter's `fp_sid`) rides in its own
// cookie, written only together with a fresh `eden_ref`, so it always
// belongs to the code on file. Mirrors `normalizeReferralTag` /
// `referralTagFromSearch` in `@canvas/schema/referrals` (the landing build
// does not import @canvas/schema). Keep in sync.
const REFERRAL_TAG_COOKIE_NAME = "eden_ref_tag";
const REFERRAL_TAG_RE = /^[a-z0-9][a-z0-9_-]{0,39}$/;
const REFERRAL_TAG_PARAMS = ["tag", "fp_sid"];

function referralTagFromParams(params: URLSearchParams): string | null {
  for (const key of REFERRAL_TAG_PARAMS) {
    const tag = params.get(key)?.trim().toLowerCase() ?? "";
    if (REFERRAL_TAG_RE.test(tag)) return tag;
  }
  return null;
}

function writeTagCookie(tag: string | null): void {
  if (typeof document === "undefined") return;
  const host = window.location.hostname;
  const isEdenProd = host === "eden.so" || host.endsWith(".eden.so");
  const domain = isEdenProd ? "; Domain=.eden.so" : "";
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  const maxAge = tag ? REFERRAL_COOKIE_DAYS * 24 * 60 * 60 : 0;
  document.cookie = `${REFERRAL_TAG_COOKIE_NAME}=${tag ? encodeURIComponent(tag) : ""}; Max-Age=${maxAge}; Path=/${domain}; SameSite=Lax${secure}`;
}

const REFERRAL_VISITOR_KEY = "eden.ref-visitor";
const API_BASE = (process.env.GATSBY_API_URL || "https://api.eden.so").replace(/\/$/, "");

/** The id a referral click is counted by (once per visitor and day), or null. */
function referralVisitorId(): string | null {
  try {
    const existing = localStorage.getItem(REFERRAL_VISITOR_KEY);
    if (existing) return existing;
    const fresh = crypto.randomUUID();
    localStorage.setItem(REFERRAL_VISITOR_KEY, fresh);
    return fresh;
  } catch {
    return null;
  }
}

/** Count the click on the referrer's dashboard. Fire-and-forget; never blocks the page. */
function sendReferralClick(code: string, tag: string | null): void {
  try {
    const visitorId = referralVisitorId();
    if (!visitorId) return;
    void fetch(`${API_BASE}/referrals/click`, {
      method: "POST",
      keepalive: true,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ code, tag, visitorId }),
    }).catch(() => {});
  } catch {
    /* Counting must never break the page. */
  }
}

/**
 * Remember the `?via=` code (and its tag) on this URL for 60 days (first
 * touch wins), and count the click for the referrer. Returns the code now on
 * file, or null. Safe to call on every page load.
 */
export function captureReferralCode(): string | null {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  const via = params.get("via")?.trim() ?? "";
  const linkCode = REFERRAL_CODE_RE.test(via) ? via : null;
  const linkTag = linkCode ? referralTagFromParams(params) : null;
  if (linkCode) sendReferralClick(linkCode, linkTag);
  const existing = readReferralCookie();
  if (existing) return existing;
  if (!linkCode) return null;
  writeReferralCookie(linkCode);
  writeTagCookie(linkTag);
  return linkCode;
}

/** The referral code this browser arrived with, if any (null when none or expired). */
export function getReferralCode(): string | null {
  return readReferralCookie();
}

// ── The $5 page's pay-first checkout (components/sprint/) ────────────────
//
// The page opens Stripe Checkout before there is an Eden account, so the
// things the app's bootstrap would normally read from these cookies ride
// into Checkout instead (billing-server `discoverGuestFlow.ts`): the
// referral code and its tag (claimed on the new account), FirstPromoter's
// visitor id, and the first-touch record in the same `attribution_*` shape
// the app's `checkoutAttributionProps` sends.

function readNamedCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  for (const part of document.cookie.split(";")) {
    const [rawKey, ...rest] = part.split("=");
    if (rawKey?.trim() !== name) continue;
    try {
      const value = decodeURIComponent(rest.join("=").trim());
      return value || null;
    } catch {
      return null;
    }
  }
  return null;
}

/** The link tag that came with the referral code, if any. */
export function getReferralTag(): string | null {
  const tag = readNamedCookie(REFERRAL_TAG_COOKIE_NAME);
  return tag && REFERRAL_TAG_RE.test(tag) ? tag : null;
}

/**
 * The referral to carry into the app stores (appStoreLinks.ts): the code on
 * file, else this URL's own `?via=` when cookies are blocked.
 */
export function currentStoreReferral(): { code: string; tag: string | null } | null {
  const code = getReferralCode();
  if (code) return { code, tag: getReferralTag() };
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  const via = params.get("via")?.trim() ?? "";
  return REFERRAL_CODE_RE.test(via) ? { code: via, tag: referralTagFromParams(params) } : null;
}

/** FirstPromoter's visitor id (fpr.js keeps it in memory and in `_fprom_tid`). */
export function getFirstPromoterTid(): string | null {
  if (typeof window === "undefined") return null;
  const fromWindow = (window as unknown as { FPROM?: { data?: { tid?: unknown } } }).FPROM?.data
    ?.tid;
  if (typeof fromWindow === "string" && fromWindow.trim()) return fromWindow.trim();
  return readNamedCookie("_fprom_tid");
}

/** `attribution_*` (first touch), `attribution_last_*` (last touch) and the Meta click cookies, for Checkout metadata. */
export function checkoutAttribution(): Record<string, string> {
  const out: Record<string, string> = {};
  const record = readCookie();
  if (record) {
    const pairs: Array<[string, string | null]> = [
      ["attribution_source", record.source],
      ["attribution_medium", record.medium],
      ["attribution_campaign", record.campaign],
      ["attribution_content", record.content],
      ["attribution_term", record.term],
      ["attribution_captured_at", record.capturedAt],
    ];
    for (const [key, value] of pairs) if (value) out[key] = value;
  }
  const last = readLastTouch();
  if (last) {
    const pairs: Array<[string, string | null]> = [
      ["attribution_last_source", last.source],
      ["attribution_last_medium", last.medium],
      ["attribution_last_campaign", last.campaign],
      ["attribution_last_content", last.content],
      ["attribution_last_term", last.term],
      ["attribution_last_captured_at", last.capturedAt],
    ];
    for (const [key, value] of pairs) if (value) out[key] = value;
  }
  const fbp = readNamedCookie("_fbp");
  const fbc = readNamedCookie("_fbc");
  if (fbp) out.meta_fbp = fbp;
  if (fbc) out.meta_fbc = fbc;
  if (typeof navigator !== "undefined" && navigator.userAgent) {
    out.meta_ua = navigator.userAgent.slice(0, 400);
  }
  return out;
}
